import { DeleteOutlined } from "@ant-design/icons";
import { useDelete, useTranslate } from "@refinedev/core";
import { DeleteButtonProps as RefineDeleteButtonProps } from "@refinedev/antd";
import { Button, Popconfirm } from "antd";

// delete button with custom onSuccess callback executing directly after mutation success
// the refine DeleteButton would not call onSuccess callback if it's being unmounted after mutation success, e.g. when button is hidden on mouseleave
export const DeleteButton = ({
  recordItemId,
  resource,
  onSuccess,
  confirmTitle,
  confirmOkText,
  confirmCancelText,
  hideText = true,
  accessControl,
  invalidates,
  meta,
  dataProviderName,
  mutationMode,
  successNotification,
  errorNotification,
  children,
  type = "text",
  size = "small",
  icon = <DeleteOutlined />,
  danger = true,
  disabled,
  loading,
  onDelete,
  ...rest
}: RefineDeleteButtonProps & { onDelete?: () => void }) => {
  const { mutateAsync, isLoading } = useDelete();
  const t = useTranslate();

  const handleDelete = async () => {
    if (onDelete) {
      onDelete();
      return;
    }
    const response = await mutateAsync({
      resource: resource,
      id: recordItemId,
      invalidates,
      meta,
      dataProviderName,
      mutationMode,
      successNotification,
      errorNotification,
    });
    onSuccess?.(response);
  };

  return (
    <Popconfirm
      title={confirmTitle ?? t("buttons.confirm")}
      onConfirm={handleDelete}
      okText={confirmOkText ?? t("buttons.delete")}
      cancelText={confirmCancelText ?? t("buttons.cancel")}
      okButtonProps={{ danger: true }}
      disabled={isLoading || disabled}
    >
      <Button
        type={type}
        size={size}
        icon={icon}
        loading={loading ?? isLoading}
        danger={danger}
        disabled={disabled ?? isLoading}
        {...rest}
      >
        {!hideText && (children ?? t("buttons.delete"))}
      </Button>
    </Popconfirm>
  );
};
